import { Container, Graphics, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"

// The speech bubble that floats above the ember roster strip when a camp member says something the
// player can't see them say (they're outside the viewport). Its tail points down at the speaker's
// roster card, so the line is tied to a face and a name rather than floating free.
//
// ONE bubble exists and is re-aimed as the lane's occupant changes — see EmberRosterWidget, which
// owns the queue. Keeping it single is the whole clutter strategy: a bubble is far wider than the
// ~56px card it belongs to, so two of them side by side would overlap and neither would be readable.
//
// It fades in and out rather than popping, and a HANDOVER between two speakers fades the old line
// out before the new one fades in — the tail moves to a different card, so cross-dissolving in place
// would read as the bubble teleporting. The fade is driven by the roster's existing per-frame tick
// rather than the Fader utility, which spawns a setInterval per fade; this runs on every frame the
// roster already animates, and timer churn is worth avoiding on the low-end hardware this targets.

// Ember-lit palette, matching the command wheel / role picker so the whole ember UI reads as one set.
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.94
const TEXT_COLOR = 0xfff3e0

const PAD_X = 9
const PAD_Y = 7
const CORNER = 7
const TAIL_H = 8
const TAIL_HALF_W = 6
const LINE_GAP = 2

// EVERY size here is in the roster widget's local units, which the strip then draws at
// WIDGET_SCALE (0.8) — so a font set to 15 lands at ~12px on screen. 12px is deliberate: it is what
// the in-world chat bubble uses (the shared "speech" role), so a line reads the same whether
// you happen to be standing next to the speaker or reading it off the HUD.
const ROSTER_SCALE = 0.8
const SCREEN_MSG_PX = TEXT_ROLES.speech.fontSize
const SCREEN_NAME_PX = TEXT_ROLES.compactCaption.fontSize
const MSG_SIZE = Math.round(SCREEN_MSG_PX / ROSTER_SCALE)
const NAME_SIZE = Math.round(SCREEN_NAME_PX / ROSTER_SCALE)

// Wide enough for a sentence over two lines at that size, narrow enough to sit over the strip.
const MAX_W = 250
// Hard cap so a long line can't grow the bubble without bound. The longest thing anyone currently
// says (the Lord's camp-news lines) is ~60 characters.
const MAX_CHARS = 72
// The name line doesn't wrap, so an over-long name would render wider than the body it sits in and
// spill out both sides of the bubble. Cap it.
const MAX_NAME_CHARS = 18

// Rough line box for a font size — Verdana's ascent+descent plus the 1px stroke TextRenderer adds.
const lineHeight = (fontSize: number) => Math.ceil(fontSize * 1.35)

// The tallest the bubble can get: a name line plus three wrapped message lines. The roster reserves
// this much room above the strip so the follow-cam PiP sitting above it never has to move, and so a
// long line can't clip. Budgeting three lines (MAX_CHARS usually wraps to two) keeps that true even
// for an awkward wrap.
export const MAX_BUBBLE_H =
    PAD_Y * 2 + lineHeight(NAME_SIZE) + LINE_GAP + 3 * lineHeight(MSG_SIZE) + TAIL_H

// The glow is drawn as a fatter copy of the body, so the bubble's real painted extent is this much
// past its rect on every side. The clamp has to allow for it or the glow is what runs off-screen.
const GLOW_PAD = 4

export interface BubbleBox {
    left: number
    top: number
    width: number
    height: number
}

// Where the bubble body sits for a given tail and content size — pure, so the one thing that must
// never go wrong (running off the right of the screen) can be tested without a renderer.
//
// The body is centred over the tail, then slid LEFT if it would overhang `clampRight` (the roster
// strip's right edge, which is itself hard against the screen edge). It is never slid right: there
// is open screen to the left of the strip, so the bubble grows that way. The tail stays on its own
// card either way — it is what identifies the speaker.
export const layoutBubble = (contentW: number, contentH: number, tailX: number, tailY: number, clampRight: number): BubbleBox => {
    const width = Math.min(MAX_W, contentW + PAD_X * 2)
    const height = contentH + PAD_Y * 2
    const rightLimit = clampRight - GLOW_PAD
    let left = tailX - width / 2
    if (left + width > rightLimit) {
        left = rightLimit - width
    }
    return { left, top: tailY - TAIL_H - height, width, height }
}

const FADE_IN_MS = 160
const FADE_OUT_MS = 220

// What the lane wants shown this frame. `key` identifies the utterance, so the bubble can tell a
// re-aim (same line, card moved) from a handover to a different line.
export interface BubbleTarget {
    key: string
    name: string
    text: string
    tailX: number
    tailY: number
    clampRight: number
    color: number
}

export class RosterSpeechBubble extends Container {
    private bg: Graphics
    private nameText: Text
    private messageText: Text
    // The utterance currently drawn (null while nothing is), and where the fade is up to.
    private shownKey: string | null = null
    private fade = 0

    constructor() {
        super()
        this.bg = new Graphics()
        this.nameText = new TextRenderer().role("compactCaption").fontSize(NAME_SIZE).render("") as Text
        this.messageText = new TextRenderer().role("speech")
            .fontSize(MSG_SIZE)
            .wordWrap(MAX_W - PAD_X * 2)
            .align("center")
            .render("") as Text
        // Top-centred so the two lines stack cleanly under one centre line.
        this.nameText.anchor.set(0.5, 0)
        this.messageText.anchor.set(0.5, 0)
        this.messageText.tint = TEXT_COLOR
        this.addChild(this.bg, this.nameText, this.messageText)
        this.visible = false
    }

    // Drive one frame of the bubble toward `target` (null = nothing to say). Handles the fade and
    // the swap, so the caller can simply hand over whatever the lane picked each frame.
    update = (dtMs: number, target: BubbleTarget | null) => {
        const wantsSwap = target !== null && this.shownKey !== null && target.key !== this.shownKey
        // Fade OUT when there's nothing to show, or when a different speaker is taking the lane —
        // the new line only appears once the old one has cleared.
        if (target === null || wantsSwap) {
            this.fade = Math.max(0, this.fade - dtMs / FADE_OUT_MS)
            if (this.fade <= 0) {
                this.shownKey = null
                this.visible = false
            }
            this.alpha = this.fade
            return
        }

        if (this.shownKey !== target.key) {
            this.draw(target)
            this.shownKey = target.key
            this.visible = true
        }
        this.fade = Math.min(1, this.fade + dtMs / FADE_IN_MS)
        this.alpha = this.fade
    }

    // Lay the bubble out around its tail. `tailX`/`tailY` are the tail TIP in the parent's
    // coordinate space (the top-centre of the speaking card); the body is drawn above it, and
    // `clampRight` keeps it from spilling past the right edge of the roster strip.
    private draw = (target: BubbleTarget) => {
        const { name, text, tailX, tailY, clampRight, color } = target
        const trimmed = text.length > MAX_CHARS ? text.slice(0, MAX_CHARS - 1) + "…" : text
        if (this.messageText.text !== trimmed) {
            this.messageText.text = trimmed
        }
        const shortName = name.length > MAX_NAME_CHARS ? name.slice(0, MAX_NAME_CHARS - 1) + "…" : name
        if (this.nameText.text !== shortName) {
            this.nameText.text = shortName
        }
        this.nameText.tint = color

        const contentW = Math.max(this.nameText.width, this.messageText.width)
        const contentH = this.nameText.height + LINE_GAP + this.messageText.height
        const box = layoutBubble(contentW, contentH, tailX, tailY, clampRight)
        const { left: bodyLeft, top: bodyTop, width: bodyW, height: bodyH } = box

        this.drawBubble(bodyLeft, bodyTop, bodyW, bodyH, tailX, tailY, color)

        const centerX = bodyLeft + bodyW / 2
        this.nameText.position.set(centerX, bodyTop + PAD_Y)
        this.messageText.position.set(centerX, bodyTop + PAD_Y + this.nameText.height + LINE_GAP)
    }

    // Body + tail drawn as ONE filled path so there's no seam where they meet: the tail's fill is
    // tucked a pixel up into the body, and only its two diagonals are stroked.
    private drawBubble = (
        left: number,
        top: number,
        w: number,
        h: number,
        tailX: number,
        tailY: number,
        color: number,
    ) => {
        const g = this.bg
        g.clear()

        // Soft outer glow — a fatter, low-alpha copy. Cheaper than a blur filter, which matters on
        // the low-end GPUs this game targets.
        g.beginFill(color, 0.06)
        g.drawRoundedRect(left - 4, top - 4, w + 8, h + 8, CORNER + 4)
        g.endFill()

        g.lineStyle(1.5, color, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(left, top, w, h, CORNER)
        g.endFill()

        // Tail fill (base tucked 1px up into the body so the join is hidden).
        const baseY = top + h - 1
        const tipX = Math.max(left + CORNER + TAIL_HALF_W, Math.min(left + w - CORNER - TAIL_HALF_W, tailX))
        g.lineStyle(0)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.moveTo(tipX - TAIL_HALF_W, baseY)
        g.lineTo(tipX + TAIL_HALF_W, baseY)
        g.lineTo(tailX, tailY)
        g.closePath()
        g.endFill()

        // Only the two diagonals are stroked — stroking the base would draw a line across the body.
        g.lineStyle(1.5, color, 0.9)
        g.moveTo(tipX - TAIL_HALF_W, baseY)
        g.lineTo(tailX, tailY)
        g.lineTo(tipX + TAIL_HALF_W, baseY)
    }
}
